// pages/History.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { APP_CONFIG } from "../app-config";
import BannerCarousel from "../components/banner-carousel";
import SidebarHeader from "../components/SidebarHeader";
import BottomNav from "../components/bottom-menu";
import toast, { Toaster } from 'react-hot-toast';
import { apiCall } from "../utils/api";
import Bmnemonic_PNG from "../assets/image/Bmnemonic_PNG.png"
import { FiArrowLeft } from "react-icons/fi";

const COLORS = APP_CONFIG.colors;

function History() {
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState([]);
    const [activeTab, setActiveTab] = useState("all");
    const [activeDateFilter, setActiveDateFilter] = useState("Today");
    const [loading, setLoading] = useState(true);
    const [showFilterModal, setShowFilterModal] = useState(false);

    // Filter States
    const [appliedFilters, setAppliedFilters] = useState({
        category: '0',
        dateFrom: '',
        dateTo: '',
        number: '',
        transactionId: '',
        status: 'All'
    });

    const [tempFilters, setTempFilters] = useState({
        category: '0',
        dateFrom: '',
        dateTo: '',
        number: '',
        transactionId: '',
        status: 'All'
    });

    // Categories and Options
    const categories = [
        { id: '0', name: 'All', icon: 'apps-outline', apiValue: '0' },
        { id: '2', name: 'Prepaid Recharge', icon: 'phone-portrait-outline', apiValue: '2' },
        { id: '3', name: 'Bill Payment', icon: 'document-text-outline', apiValue: '3' },
        { id: '1', name: 'Topup', icon: 'add-circle-outline', apiValue: '1' },
        { id: '4', name: 'Bonus', icon: 'gift-outline', apiValue: '4' },
        { id: '-1', name: 'Others', icon: 'grid-outline', apiValue: '-1' },
    ];

    const statusOptions = ['All', 'Success', 'Failed', 'Pending'];
    const dateRangeOptions = ['Today', 'Last 7 days', 'Last 15 days', 'Last 30 days', 'Custom'];

    // Tab configuration with icons
    const tabs_list = [
        { id: 'all', label: 'All', icon: '📋' },
        { id: 'recharge', label: 'Recharge', icon: '📱' },
        { id: 'billpayment', label: 'Bill Payment', icon: '📄' },
        { id: 'topup', label: 'Topup', icon: '➕' },
        { id: 'bonus', label: 'Bonus', icon: '🎁' },
    ];

    const dateFilters = ["Today", "Last 7 days", "Last 15 days", "Last 30 days", "Custom"];

    // Map tab IDs to category values
    const tabCategoryMap = {
        'all': '0',
        'recharge': '2',
        'billpayment': '3',
        'topup': '1',
        'bonus': '4'
    };

    // Initialize date range for modal
    useEffect(() => {
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0];
        setTempFilters(prev => ({
            ...prev,
            dateFrom: formattedDate,
            dateTo: formattedDate
        }));
    }, []);

    useEffect(() => {
        fetchHistory();
    }, [activeTab, activeDateFilter, appliedFilters]);

    const fetchHistory = async () => {
        try {
            setLoading(true);
            const params = {};

            // Category filter - use selected tab
            const categoryValue = tabCategoryMap[activeTab] || '0';
            if (categoryValue !== '0') {
                params.category = categoryValue;
            }

            // Status filter
            if (appliedFilters.status && appliedFilters.status !== 'All') {
                params.status = appliedFilters.status.toLowerCase();
            }

            // Number filter
            if (appliedFilters.number) {
                params.number = appliedFilters.number;
            }

            // Transaction ID filter
            if (appliedFilters.transactionId) {
                params.rrn = appliedFilters.transactionId;
            }

            // Date range filter
            if (appliedFilters.dateFrom) {
                params.date_from = appliedFilters.dateFrom;
            }
            if (appliedFilters.dateTo) {
                params.date_to = appliedFilters.dateTo;
            }

            // If no custom date range, apply date range preset
            if (!appliedFilters.dateFrom && !appliedFilters.dateTo && activeDateFilter !== 'Custom') {
                const dateRange = getDateRange(activeDateFilter);
                if (dateRange) {
                    params.date_from = dateRange.from;
                    params.date_to = dateRange.to;
                }
            }

            const result = await apiCall(
                "history",
                params,
                true,
                {},
                null
            );

            if (result.status && result.history) {
                const formattedTransactions = result.history.map((item, index) => ({
                    id: item.order_id || index,
                    category: item.order_type,
                    type: item.order_type,
                    number: item.cn,
                    amount: item.amount,
                    time: item.time,
                    status: item.status,
                    operator: item.biller_name,
                    operatorIcon: item.logo,
                    rrn: item.txn,
                    date: item.time // Assuming time contains date
                }));
                setTransactions(formattedTransactions);
            } else {
                if (result.response_code !== 2) {
                    toast.error(result.message || 'Failed to fetch transactions');
                }
                setTransactions([]);
            }
        } catch (error) {
            toast.error('Error fetching report');
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    const getDateRange = (filter) => {
        const today = new Date();
        const from = new Date(today);
        const to = new Date(today);
        to.setHours(23, 59, 59, 999);

        switch (filter) {
            case 'Today':
                from.setHours(0, 0, 0, 0);
                to.setHours(23, 59, 59, 999);
                break;
            case 'Last 7 days':
                from.setDate(today.getDate() - 7);
                from.setHours(0, 0, 0, 0);
                break;
            case 'Last 15 days':
                from.setDate(today.getDate() - 15);
                from.setHours(0, 0, 0, 0);
                break;
            case 'Last 30 days':
                from.setDate(today.getDate() - 30);
                from.setHours(0, 0, 0, 0);
                break;
            case 'Custom':
                return null;
            default:
                return null;
        }

        return {
            from: from.toISOString().split('T')[0],
            to: to.toISOString().split('T')[0]
        };
    };

    const getStatusDisplay = (status) => {
        switch (status) {
            case "1":
                return { bg: COLORS.primaryColor, label: "Success", color: COLORS.textOnPrimary };
            case "2":
                return { bg: COLORS.errorColor, label: "Failed", color: COLORS.textOnPrimary };
            case "0":
                return { bg: COLORS.primaryLight, label: "Pending", color: COLORS.textOnPrimary };
            case "3":
                return { bg: COLORS.primaryLight, label: "Pending", color: COLORS.textOnPrimary };
            default:
                return { bg: COLORS.textSecondary, label: "Unknown", color: COLORS.textOnPrimary };
        }
    };

    const filterByType = (transactions) => {
        if (activeTab === "all") return transactions;

        const categoryId = tabCategoryMap[activeTab];
        return transactions.filter(txn => txn.category === categoryId);
    };

    const filtered = filterByType(transactions);

    const groupByDate = (txns) => {
        const groups = {};
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        txns.forEach((txn) => {
            const d = new Date(txn.date);
            let label;
            if (d.toDateString() === today.toDateString()) {
                label = "Today";
            } else if (d.toDateString() === yesterday.toDateString()) {
                label = "Yesterday";
            } else {
                label = d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
            }
            if (!groups[label]) groups[label] = [];
            groups[label].push(txn);
        });

        const sortedGroups = {};
        Object.keys(groups).sort((a, b) => {
            if (a === "Today") return -1;
            if (b === "Today") return 1;
            if (a === "Yesterday") return -1;
            if (b === "Yesterday") return 1;
            return new Date(b) - new Date(a);
        }).forEach(key => {
            sortedGroups[key] = groups[key];
        });

        return sortedGroups;
    };

    const grouped = groupByDate(filtered);

    const formatTime = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    };

    const formatAmount = (amount) => {
        const amt = parseFloat(amount);
        return isNaN(amt) ? 0 : amt.toFixed(2);
    };

    const truncateBillerName = (name) => {
        if (!name) return "";
        return name.length > 10 ? name.substring(0, 10) + "..." : name;
    };

    const handleFilterApply = () => {
        setAppliedFilters({
            category: tempFilters.category,
            dateFrom: tempFilters.dateFrom,
            dateTo: tempFilters.dateTo,
            number: tempFilters.number,
            transactionId: tempFilters.transactionId,
            status: tempFilters.status
        });
        setShowFilterModal(false);
        if (tempFilters.dateFrom && tempFilters.dateTo) {
            setActiveDateFilter('Custom');
        }
    };

    const handleFilterReset = () => {
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0];
        const resetFilters = {
            category: '0',
            dateFrom: formattedDate,
            dateTo: formattedDate,
            number: '',
            transactionId: '',
            status: 'All'
        };
        setTempFilters(resetFilters);
        setAppliedFilters(resetFilters);
        setActiveDateFilter('Today');
        setShowFilterModal(false);
    };

    const handleDateFilterChange = (filter) => {
        setActiveDateFilter(filter);
        if (filter === 'Custom') {
            setShowFilterModal(true);
            // Set default date range to today
            const today = new Date();
            const formattedDate = today.toISOString().split('T')[0];
            setTempFilters(prev => ({
                ...prev,
                dateFrom: formattedDate,
                dateTo: formattedDate
            }));
        } else {
            // Clear custom date filters when using preset
            setAppliedFilters(prev => ({
                ...prev,
                dateFrom: '',
                dateTo: ''
            }));
            setTempFilters(prev => ({
                ...prev,
                dateFrom: '',
                dateTo: ''
            }));
        }
    };

    // Filter Modal Component
    const FilterModal = () => {
        if (!showFilterModal) return null;

        return (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={() => setShowFilterModal(false)}
            >
                <div
                    className="w-full max-w-md rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
                    style={{ background: COLORS.surfaceColor }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <h3 className="text-lg font-bold mb-4" style={{ color: COLORS.textPrimary }}>
                        Custom Date Range
                    </h3>

                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium block mb-1" style={{ color: COLORS.textSecondary }}>
                                From Date
                            </label>
                            <input
                                type="date"
                                value={tempFilters.dateFrom}
                                onChange={(e) => setTempFilters({ ...tempFilters, dateFrom: e.target.value })}
                                className="w-full px-4 py-2 rounded-xl border text-sm"
                                style={{
                                    background: COLORS.bgColor,
                                    borderColor: COLORS.borderColor,
                                    color: COLORS.textPrimary
                                }}
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium block mb-1" style={{ color: COLORS.textSecondary }}>
                                To Date
                            </label>
                            <input
                                type="date"
                                value={tempFilters.dateTo}
                                onChange={(e) => setTempFilters({ ...tempFilters, dateTo: e.target.value })}
                                className="w-full px-4 py-2 rounded-xl border text-sm"
                                style={{
                                    background: COLORS.bgColor,
                                    borderColor: COLORS.borderColor,
                                    color: COLORS.textPrimary
                                }}
                            />
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={handleFilterReset}
                                className="flex-1 py-2 rounded-xl text-sm font-semibold"
                                style={{
                                    background: COLORS.borderColor,
                                    color: COLORS.textSecondary
                                }}
                            >
                                Reset
                            </button>
                            <button
                                onClick={handleFilterApply}
                                className="flex-1 py-2 rounded-xl text-sm font-semibold"
                                style={{
                                    background: COLORS.primaryColor,
                                    color: COLORS.textOnPrimary
                                }}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <SidebarHeader>
            <div className="flex items-center gap-3 pb-4">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
                >
                    <FiArrowLeft className="text-xl" />
                </button>
                <h1 className="text-lg font-semibold text-gray-800">
                    History
                </h1>
            </div>
            <div className="max-w-7xl mx-auto mb-16">
                {/* Date Filter Tabs */}
                <div className="overflow-x-auto scrollbar-hide pt-4 mb-4">
                    <div className="flex gap-2.5 min-w-max">
                        {dateFilters.map((df) => (
                            <button
                                key={df}
                                onClick={() => handleDateFilterChange(df)}
                                disabled={loading}
                                className="px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                    border: `1.5px solid ${activeDateFilter === df ? COLORS.primaryColor : COLORS.borderColor}`,
                                    background: activeDateFilter === df ? `${COLORS.primaryColor}10` : "transparent",
                                    color: activeDateFilter === df ? COLORS.primaryColor : COLORS.textSecondary,
                                }}
                            >
                                {df}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Simple Tabs with Icons */}
                <div
                    className="mb-5 overflow-x-auto border-b"
                    style={{ borderColor: COLORS.borderColor }}
                >
                    <div className="flex min-w-max">
                        {tabs_list.map((tab) => {
                            const isActive = activeTab === tab.id;

                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className="relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-200 whitespace-nowrap"
                                    style={{
                                        color: isActive
                                            ? COLORS.primaryColor
                                            : COLORS.textSecondary,
                                    }}
                                >
                                    {/* Icon */}
                                    <span
                                        className="flex items-center justify-center"
                                        style={{
                                            opacity: isActive ? 1 : 0.65,
                                        }}
                                    >
                                        {tab.icon}
                                    </span>

                                    {/* Label */}
                                    <span>{tab.label}</span>

                                    {/* Active Indicator */}
                                    {isActive && (
                                        <span
                                            className="absolute bottom-0 left-0 right-0 h-[2px] rounded-t-full"
                                            style={{
                                                backgroundColor: COLORS.primaryColor,
                                            }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>


                {/* Loading State */}
                <div
                    className="rounded-2xl p-4 md:p-6"
                    style={{
                        background: COLORS.bgColor,
                        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                        border: `1px solid ${COLORS.borderColor}`,
                    }}
                >
                    {loading ? (
                        <div className="flex items-center justify-center min-h-[60vh]">
                            <div className="flex flex-col items-center gap-3">
                                <div
                                    className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin"
                                    style={{
                                        borderColor: `${COLORS.primaryColor}33`,
                                        borderTopColor: COLORS.primaryColor,
                                    }}
                                />
                                <div className="text-sm font-medium" style={{ color: COLORS.textPrimary }}>
                                    Loading transactions...
                                </div>
                            </div>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-16">
                            <div
                                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                                style={{ background: COLORS.surfaceColor }}
                            >
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="1.5">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="16" y1="13" x2="8" y2="13" />
                                    <line x1="16" y1="17" x2="8" y2="17" />
                                </svg>
                            </div>
                            <p className="text-base font-semibold mb-1" style={{ color: COLORS.textPrimary }}>
                                No transactions found
                            </p>
                            <p className="text-xs" style={{ color: COLORS.textSecondary }}>
                                No transactions match your filters
                            </p>
                        </div>
                    ) : (
                        Object.entries(grouped).map(([dateLabel, txns]) => (
                            <div key={dateLabel}>
                                <div
                                    className="text-xs font-semibold uppercase my-4"
                                    style={{
                                        color: COLORS.textSecondary,
                                        letterSpacing: "0.5px",
                                    }}
                                >
                                    {dateLabel}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {txns.map((txn) => {
                                        const statusDisplay = getStatusDisplay(txn.status);
                                        const amount = formatAmount(txn.amount);
                                        const truncatedName = txn.operator
                                            ? truncateBillerName(txn.operator)
                                            : txn.category;

                                        return (
                                            <div
                                                key={txn.id}
                                                onClick={() => navigate("/transaction-detail", { state: { txn } })}
                                                className="relative flex items-center gap-3 p-4 rounded-xl border cursor-pointer mb-3 overflow-hidden transition-transform duration-100 active:scale-98"
                                                style={{
                                                    background: COLORS.surfaceColor,
                                                    borderColor: COLORS.borderColor,
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                                                }}
                                            >
                                                {/* Logo */}
                                                <div
                                                    className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                                                    style={{ background: `${COLORS.primaryColor}15` }}
                                                >
                                                    {txn.operatorIcon ? (
                                                        <img
                                                            src={txn.operatorIcon}
                                                            alt={txn.operator}
                                                            className="w-8 h-8 object-contain"
                                                        />
                                                    ) : (
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={COLORS.primaryColor} strokeWidth="2">
                                                            <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                                                            <line x1="12" y1="18" x2="12.01" y2="18" />
                                                        </svg>
                                                    )}
                                                </div>

                                                {/* Details */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs font-bold" style={{ color: COLORS.textPrimary }}>
                                                        {truncatedName} · {formatTime(txn.time)}
                                                    </div>
                                                    <div className="text-xs mt-0.5 opacity-80" style={{ color: COLORS.textSecondary }}>
                                                        {txn.number}
                                                    </div>
                                                </div>

                                                {/* Amount */}
                                                <div
                                                    className="text-sm font-extrabold flex-shrink-0"
                                                    style={{ color: COLORS.textPrimary }}
                                                >
                                                    ₹{amount}
                                                </div>

                                                {/* Status Badge */}
                                                <div
                                                    className="absolute top-0 right-0 text-[9px] font-extrabold uppercase py-1 px-2.5"
                                                    style={{
                                                        color: statusDisplay.color,
                                                        background: statusDisplay.bg,
                                                        borderBottomLeftRadius: "12px",
                                                        borderTopRightRadius: "12px",
                                                        letterSpacing: "0.5px",
                                                    }}
                                                >
                                                    {statusDisplay.label}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Filter Modal */}
            <FilterModal />

            <Toaster />

            {/* Bottom Navigation - Hidden on desktop (lg and above) */}
            <div className="lg:hidden">
                <BottomNav />
            </div>

            <style>
                {`
                    .active\\:scale-98:active {
                        transform: scale(0.98);
                    }
                    .active\\:scale-95:active {
                        transform: scale(0.95);
                    }
                    .scrollbar-hide::-webkit-scrollbar {
                        display: none;
                    }
                    .scrollbar-hide {
                        -ms-overflow-style: none;
                        scrollbar-width: none;
                    }
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                    .animate-spin {
                        animation: spin 0.8s linear infinite;
                    }
                    .truncate {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                `}
            </style>
        </SidebarHeader>
    );
}

export default History;